import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { auth } from "../services/firebase";
import { usePageTracking } from "@/hooks/usePageTracking";

const EmailVerificationPage: React.FC = () => {
  usePageTracking("EmailVerification", "EmailVerificationPage");
  const { currentUser, resendVerificationEmail, isEmailVerified, logout } =
    useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [isResending, setIsResending] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [canResend, setCanResend] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);

  // Check verification status periodically
  useEffect(() => {
    const checkInterval = setInterval(async () => {
      // Force reload the user to get fresh verification status from Firebase
      if (auth.currentUser) {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          addToast("Email verified successfully!", "success");
          navigate("/");
        }
      }
    }, 3000); // Check every 3 seconds

    return () => clearInterval(checkInterval);
  }, [navigate, addToast]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleResend = async () => {
    if (!canResend) return;

    setIsResending(true);
    try {
      await resendVerificationEmail();
      addToast(
        "Verification email sent! Check your inbox. (Please check spam folder as well)",
        "success",
      );
      setCanResend(false);
      setResendTimer(60); // 60 second cooldown
    } catch (error: any) {
      addToast(error.message || "Failed to send verification email", "error");
    } finally {
      setIsResending(false);
    }
  };

  const handleCheckStatus = async () => {
    setIsChecking(true);
    try {
      if (!auth.currentUser) {
        throw new Error("No user is currently signed in");
      }
      // Force reload the Firebase user to get the latest verification status
      if (auth.currentUser) {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          addToast("Email verified successfully!", "success");
          navigate("/");
        } else {
          addToast("Email not verified yet. Please check your inbox.", "info");
        }
      }
    } catch (error) {
      if (!auth.currentUser) {
        addToast(error?.message, "error");
      } else {
        addToast("Failed to check verification status", "error");
      }
    } finally {
      setIsChecking(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      addToast("Failed to logout", "error");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center">
            <span className="text-5xl">📧</span>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-center text-gray-900 mb-2">
          Verify Your Email
        </h1>
        <p className="text-center text-gray-600 mb-8">
          We sent a verification email to:
        </p>

        {/* Email Display */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
          <p className="text-center font-medium text-gray-900 break-all">
            {currentUser?.email}
          </p>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <span className="text-blue-600 mr-3 flex-shrink-0 mt-0.5 text-xl">
              ℹ️
            </span>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Check your inbox</p>
              <p>
                Click the verification link in the email to continue. The page
                will automatically update once verified.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Check Status Button */}
          <button
            onClick={handleCheckStatus}
            disabled={isChecking}
            className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isChecking ? (
              <>
                <span className="mr-2 inline-block animate-spin">🔄</span>
                Checking...
              </>
            ) : (
              <>
                <span className="mr-2">✅</span>
                I've Verified My Email
              </>
            )}
          </button>

          {/* Resend Button */}
          <button
            onClick={handleResend}
            disabled={isResending || !canResend}
            className="w-full bg-white border-2 border-purple-600 text-purple-600 py-3 rounded-lg font-medium hover:bg-purple-50 transition-colors disabled:bg-gray-100 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isResending ? (
              <>
                <span className="mr-2 inline-block animate-spin">🔄</span>
                Sending...
              </>
            ) : !canResend ? (
              `Resend in ${resendTimer}s`
            ) : (
              <>
                <span className="mr-2">🔄</span>
                Resend Verification Email
              </>
            )}
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Didn't receive the email? Check your spam folder.</p>
        </div>

        {/* Logout Link */}
        <div className="mt-6 text-center">
          <button
            onClick={handleLogout}
            className="text-sm text-gray-600 hover:text-gray-900 underline"
          >
            Log out and use a different account
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
